/**
 * Last-resort guard around the whole app.
 *
 * A render error in React unmounts the entire tree, which without a boundary
 * leaves a blank white page: the user cannot tell the app from their network,
 * and the one piece of information worth having — the stack — is only in a
 * console they will not open. This boundary turns that into a readable screen
 * with a report link carrying the stack, so a crash arrives as a filed issue
 * rather than as silence.
 */
import React from "react";

import ReportIssueLink from "./ReportIssueLink";

interface ErrorBoundaryProps {
  readonly children: React.ReactNode;
}

interface ErrorBoundaryState {
  /** The caught error, or null while the tree is healthy. */
  readonly error: Error | null;
  /** React's component stack, which locates the crash better than the JS one. */
  readonly componentStack: string | null;
}

/**
 * Combines the JS stack with React's component stack into the trace attached
 * to a crash report. Either may be missing depending on the browser and build.
 */
function crashTrace(error: Error, componentStack: string | null): string {
  const sections = [error.stack ?? String(error)];
  if (componentStack !== null) {
    sections.push(`Component stack:${componentStack}`);
  }
  return sections.join("\n\n");
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public override state: ErrorBoundaryState = {
    error: null,
    componentStack: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, componentStack: null };
  }

  public override componentDidCatch(
    error: Error,
    errorInfo: React.ErrorInfo,
  ): void {
    // Keep the crash in the console as well as on screen: the report link only
    // helps if the user chooses to use it, and a developer reproducing locally
    // wants the untruncated trace.
    console.error("Unhandled error in React tree", error, errorInfo);
    this.setState({ error, componentStack: errorInfo.componentStack ?? null });
  }

  public override render(): React.ReactNode {
    const { error, componentStack } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="error-container error-boundary" role="alert">
        <h2>Something went wrong</h2>
        <p>
          The app hit an unexpected error and had to stop rendering. Reporting
          this helps get it fixed — the report is prefilled with the error
          details.
        </p>
        <pre className="error-boundary-message">{error.message}</pre>
        <div className="error-boundary-actions">
          <ReportIssueLink
            className="error-boundary-report"
            details={{
              message: error.message,
              stack: crashTrace(error, componentStack),
            }}
          >
            Report this crash
          </ReportIssueLink>
          <button
            type="button"
            className="sim-btn"
            onClick={() => {
              window.location.reload();
            }}
            title="Reload the app and try again"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
